/**
 * Free Shipping Progress - Auto-add/remove free gift based on threshold
 * When a tier with reward type "free gift" is qualified, adds that product to the cart.
 * When threshold is no longer met, removes the free gift from the cart.
 *
 * Glowora: a free gift is auto-added only ONCE. If the shopper removes it from the cart
 * (in the drawer, on the cart page or in checkout) while still above the threshold, that
 * is remembered on this device and the gift is never auto-added again, also not in
 * future orders. A gift the script itself removed (cart dropped below the threshold)
 * does not count as declined, so it comes back when the threshold is reached again.
 * A free gift is always quantity 1; a higher quantity is set back to 1.
 *
 * Concurrency: cart changes made by the shopper while a gift add/remove is in flight are
 * never dropped. They queue a re-sync, and a gift response that is older than the shopper's
 * latest change is not morphed into the cart (the cart is re-rendered fresh instead), so the
 * cart total and free-shipping bar always reflect the current cart.
 */
import { CartAddEvent, CartUpdateEvent, ThemeEvents } from '@theme/events';
import { DialogOpenEvent } from '@theme/dialog';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import { fetchConfig } from '@theme/utilities';

let isModifyingGift = false;
let runAddGiftsScheduled = false;
/** A sync was requested while another one was running; run again when it finishes. */
let resyncRequested = false;
/** Incremented on every cart update not made by this script. */
let externalUpdateCount = 0;
/** A gift response was discarded as stale; the cart sections must be re-rendered fresh. */
let needsFreshRender = false;

/* ------------------------------------------------------------------ */
/* Glowora: remember auto-added and declined gifts                     */
/* ------------------------------------------------------------------ */

/** Variant ids the shopper removed. Permanent on this device. */
const DECLINED_KEY = 'glw_free_gift_declined';
/** Gifts this script added, per cart token: { token: string, variants: string[] }. */
const ADDED_KEY = 'glw_free_gift_added';

/** In-memory fallback for browsers where storage is blocked (private mode etc.). */
const memoryStore = {};

function storageGet(key) {
  try {
    const value = window.localStorage.getItem(key);
    if (value !== null) return value;
  } catch (e) {
    /* storage blocked */
  }
  return Object.prototype.hasOwnProperty.call(memoryStore, key) ? memoryStore[key] : null;
}

function storageSet(key, value) {
  memoryStore[key] = value;
  try {
    window.localStorage.setItem(key, value);
  } catch (e) {
    /* storage blocked, memory fallback still works for this page view */
  }
}

function readJson(key, fallback) {
  const raw = storageGet(key);
  if (!raw) return fallback;
  try {
    const parsed = JSON.parse(raw);
    return parsed ?? fallback;
  } catch (e) {
    return fallback;
  }
}

/** @returns {Set<string>} */
function getDeclinedGifts() {
  const list = readJson(DECLINED_KEY, []);
  return new Set(Array.isArray(list) ? list.map(String) : []);
}

/** @param {string} variantId */
function markGiftDeclined(variantId) {
  const declined = getDeclinedGifts();
  if (declined.has(variantId)) return;
  declined.add(variantId);
  storageSet(DECLINED_KEY, JSON.stringify(Array.from(declined)));
}

/**
 * @param {string} cartToken
 * @returns {Set<string>} Gift variants auto-added to this specific cart.
 */
function getAddedGifts(cartToken) {
  const state = readJson(ADDED_KEY, null);
  if (!state || typeof state !== 'object' || state.token !== cartToken || !Array.isArray(state.variants)) {
    return new Set();
  }
  return new Set(state.variants.map(String));
}

/**
 * @param {string} cartToken
 * @param {Set<string>} variants
 */
function saveAddedGifts(cartToken, variants) {
  storageSet(ADDED_KEY, JSON.stringify({ token: cartToken, variants: Array.from(variants) }));
}

/** Cart token without the `?key=` suffix Shopify sometimes appends. */
function getCartToken(cart) {
  return String(cart?.token || '').split('?')[0];
}

/* ------------------------------------------------------------------ */

/**
 * Resolve `/cart.js` with locale/markets prefix (`Shopify.routes.root`), matching `_upsell-product.liquid`.
 * `Theme.routes.cart_url` is the HTML cart page — never fetch it expecting JSON.
 */
function getCartJsUrl() {
  const root = window.Shopify?.routes?.root;
  if (typeof root === 'string' && root.length > 0) {
    return `${root.endsWith('/') ? root : `${root}/`}cart.js`;
  }
  const base = window.Theme?.routes?.cart_url;
  if (typeof base === 'string' && base.length > 0) {
    return base.replace(/\/?$/, '') + '.js';
  }
  return '/cart.js';
}

/** Liquid exposes `/cart/change`; Cart AJAX expects `.js`. Theme often mirrors `cart_add_url`. */
function getCartChangeJsUrl() {
  const raw = window.Theme?.routes?.cart_change_url ?? '/cart/change.js';
  const url = String(raw).trim();
  return url.includes('.js') ? url : url.replace(/\/?$/, '') + '.js';
}

function getCartAddJsUrl() {
  const raw = window.Theme?.routes?.cart_add_url ?? '/cart/add.js';
  const url = String(raw).trim();
  return url.includes('.js') ? url : url.replace(/\/?$/, '') + '.js';
}

/**
 * Wrong endpoints and password storefronts return HTML; avoid SyntaxError from `.json()`.
 * @param {Response} response
 * @param {string} label
 */
async function readCartApiJson(response, label) {
  const text = await response.text();
  const trimmed = text.trimStart();
  if (trimmed.startsWith('<')) {
    console.warn(
      `[free-shipping-progress] ${label}: expected JSON, got HTML (status ${response.status}).`,
      trimmed.slice(0, 160)
    );
    return null;
  }
  try {
    return JSON.parse(text);
  } catch (e) {
    console.warn(`[free-shipping-progress] ${label}: JSON parse failed`, e);
    return null;
  }
}

/** @returns {string[]} */
function getCartSectionIds() {
  return Array.from(document.querySelectorAll('cart-items-component'))
    .map((el) => el.dataset.sectionId)
    .filter(Boolean);
}

/**
 * Re-renders every cart section from the server, bypassing the section cache.
 * @param {string[]} sectionIds
 */
function renderSectionsFresh(sectionIds) {
  for (const id of sectionIds) {
    sectionRenderer.renderSection(id, { cache: false }).catch((err) => {
      console.warn('[free-shipping-progress] section render failed', err);
    });
  }
}

/**
 * Morphs the sections returned by a gift add/remove, unless the shopper changed the cart
 * after this sync started; that response would be older than what is on screen.
 * @param {Record<string, string> | undefined} sections
 * @param {string[]} sectionIds
 * @param {number} startCount
 * @returns {boolean} Whether the response sections were fresh and applied.
 */
function applyGiftSections(sections, sectionIds, startCount) {
  if (externalUpdateCount !== startCount) {
    needsFreshRender = true;
    return false;
  }

  const ids = sectionIds.filter((id) => sections?.[id]);
  if (ids.length === 0) {
    renderSectionsFresh(sectionIds);
    return false;
  }

  for (const id of ids) {
    try {
      morphSection(id, sections[id]);
    } catch (err) {
      console.warn('[free-shipping-progress] morph failed', err);
    }
  }
  return true;
}

async function syncFreeGifts() {
  const progressEl = document.querySelector('.free-shipping-progress[data-gift-variant-ids]');
  if (!progressEl) return;

  const giftVariantIdsStr = progressEl.dataset.giftVariantIds;
  const giftThresholdsStr = progressEl.dataset.giftThresholds;
  if (!giftVariantIdsStr || !giftThresholdsStr) return;

  const cartItemsComponent = progressEl.closest('cart-items-component');
  const sectionId = cartItemsComponent?.dataset?.sectionId;
  if (!sectionId) return;

  if (isModifyingGift) {
    resyncRequested = true;
    return;
  }

  const variantIds = giftVariantIdsStr.split(',').map((id) => id.trim()).filter(Boolean);
  const thresholdParts = giftThresholdsStr.split(',').map((t) => parseInt(t.trim(), 10) || 0);
  if (variantIds.length === 0 || variantIds.length !== thresholdParts.length) return;

  const giftConfig = variantIds.map((vid, i) => ({
    variantId: vid,
    thresholdCents: thresholdParts[i] || 0,
  }));

  isModifyingGift = true;
  const startCount = externalUpdateCount;
  const sectionIds = getCartSectionIds();

  try {
    const cartRes = await fetch(getCartJsUrl(), {
      headers: { Accept: 'application/json' },
      cache: 'no-store',
    });
    const cart = await readCartApiJson(cartRes, 'GET cart.js');
    if (!cart || typeof cart !== 'object') return;

    // The shopper changed the cart while we were reading it; start over with fresh data.
    if (externalUpdateCount !== startCount) {
      resyncRequested = true;
      return;
    }

    const currentTotal = cart.total_price || 0;
    const cartItems = cart.items || [];
    const cartVariantIds = new Set(cartItems.map((item) => String(item.variant_id)));

    // Glowora: work out which gifts the shopper removed themselves.
    const cartToken = getCartToken(cart);
    const addedGifts = cartToken ? getAddedGifts(cartToken) : new Set();
    let addedChanged = false;

    for (const g of giftConfig) {
      if (g.thresholdCents <= 0) continue;
      const inCart = cartVariantIds.has(g.variantId);

      if (inCart) {
        if (!addedGifts.has(g.variantId)) {
          addedGifts.add(g.variantId);
          addedChanged = true;
        }
      } else if (addedGifts.has(g.variantId)) {
        // We added it to this cart and it is gone. Above the threshold the shopper removed it.
        if (currentTotal >= g.thresholdCents) markGiftDeclined(g.variantId);
        addedGifts.delete(g.variantId);
        addedChanged = true;
      }
    }

    if (cartToken && addedChanged) saveAddedGifts(cartToken, addedGifts);
    const declinedGifts = getDeclinedGifts();

    const toRemove = [];
    for (let i = 0; i < cartItems.length; i++) {
      const item = cartItems[i];
      const vid = String(item.variant_id);
      const config = giftConfig.find((g) => g.variantId === vid);
      if (config && config.thresholdCents > 0 && currentTotal < config.thresholdCents) {
        toRemove.push({ line: i + 1, variantId: vid });
      }
    }

    if (toRemove.length > 0) {
      toRemove.sort((a, b) => b.line - a.line);
      for (const { line, variantId } of toRemove) {
        const body = JSON.stringify({
          line,
          quantity: 0,
          sections: sectionIds.join(','),
          sections_url: window.location.pathname,
        });
        const response = await fetch(getCartChangeJsUrl(), fetchConfig('json', { body }));
        const data = (await readCartApiJson(response, 'POST cart/change.js')) ?? {};
        if (data.errors) {
          console.warn('Free gift remove:', data.errors);
          continue;
        }

        // Removed by us (below threshold), not by the shopper: may come back later.
        if (cartToken && addedGifts.delete(variantId)) saveAddedGifts(cartToken, addedGifts);

        const fresh = applyGiftSections(data.sections, sectionIds, startCount);
        document.dispatchEvent(
          new CartUpdateEvent(data, sectionId, {
            source: 'free-shipping-progress',
            itemCount: data.item_count ?? 0,
            // Stale sections must not be morphed by other listeners either.
            sections: fresh ? data.sections : undefined,
          })
        );
      }
      return;
    }

    // Glowora: a free gift is always exactly 1. Higher quantities (old carts, cart page,
    // typed input) are set back to 1.
    const toClamp = [];
    for (let i = 0; i < cartItems.length; i++) {
      const item = cartItems[i];
      const config = giftConfig.find((g) => g.variantId === String(item.variant_id));
      if (config && config.thresholdCents > 0 && (item.quantity || 0) > 1) {
        toClamp.push(i + 1);
      }
    }

    if (toClamp.length > 0) {
      for (const line of toClamp) {
        const body = JSON.stringify({
          line,
          quantity: 1,
          sections: sectionIds.join(','),
          sections_url: window.location.pathname,
        });
        const response = await fetch(getCartChangeJsUrl(), fetchConfig('json', { body }));
        const data = (await readCartApiJson(response, 'POST cart/change.js')) ?? {};
        if (data.errors) {
          console.warn('Free gift quantity reset:', data.errors);
          continue;
        }
        const fresh = applyGiftSections(data.sections, sectionIds, startCount);
        document.dispatchEvent(
          new CartUpdateEvent(data, sectionId, {
            source: 'free-shipping-progress',
            itemCount: data.item_count ?? 0,
            sections: fresh ? data.sections : undefined,
          })
        );
      }
      return;
    }

    const toAdd = giftConfig.filter(
      (g) =>
        g.thresholdCents > 0 &&
        !cartVariantIds.has(g.variantId) &&
        !declinedGifts.has(g.variantId) &&
        currentTotal >= g.thresholdCents
    );
    if (toAdd.length === 0) return;

    let itemCount = cart.item_count ?? cartItems.length;

    for (const { variantId: vid } of toAdd) {
      const formData = new FormData();
      formData.append('id', vid);
      formData.append('quantity', '1');
      if (sectionIds.length > 0) {
        formData.append('sections', sectionIds.join(','));
        formData.append('sections_url', window.location.pathname);
      }

      const response = await fetch(getCartAddJsUrl(), {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: formData,
      });

      const data = (await readCartApiJson(response, 'POST cart/add.js')) ?? {};

      if (data.status || !response.ok) {
        console.warn('Free gift add:', data.message || 'Failed to add');
        continue;
      }

      // Remember that we added it, so a later removal is recognised as the shopper's choice.
      if (cartToken) {
        addedGifts.add(String(vid));
        saveAddedGifts(cartToken, addedGifts);
      }

      itemCount += 1;
      const fresh = applyGiftSections(data.sections, sectionIds, startCount);

      document.dispatchEvent(
        new CartAddEvent(
          {},
          'free-shipping-progress',
          {
            source: 'free-shipping-progress',
            // Total cart count, so the cart bubble does not reset to 1.
            itemCount,
            variantId: String(vid),
            sections: fresh ? data.sections : {},
          }
        )
      );
    }
  } catch (err) {
    console.error('Free shipping progress gift sync error:', err);
  } finally {
    isModifyingGift = false;
    if (externalUpdateCount !== startCount) resyncRequested = true;

    if (resyncRequested) {
      // The re-sync reads the latest cart and re-renders if a stale response was discarded.
      resyncRequested = false;
      runSyncGifts();
    } else if (needsFreshRender) {
      needsFreshRender = false;
      renderSectionsFresh(getCartSectionIds());
    }
  }
}

function runSyncGifts() {
  if (runAddGiftsScheduled) return;
  runAddGiftsScheduled = true;
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      runAddGiftsScheduled = false;
      syncFreeGifts();
    });
  });
}

document.addEventListener(ThemeEvents.cartUpdate, (event) => {
  if (event.detail?.data?.source === 'free-shipping-progress') return;
  externalUpdateCount += 1;
  runSyncGifts();
});

document.addEventListener(DialogOpenEvent.eventName, runSyncGifts);

customElements.whenDefined('cart-items-component').then(() => {
  const observer = new MutationObserver((mutations) => {
    for (const m of mutations) {
      if (m.addedNodes.length) {
        runSyncGifts();
        break;
      }
    }
  });

  const cartDrawer = document.querySelector('cart-drawer-component');
  if (cartDrawer) {
    const dialog = cartDrawer.querySelector('dialog');
    if (dialog) {
      observer.observe(dialog, { childList: true, subtree: true });
      dialog.addEventListener('open', runSyncGifts);
    }
  }

  runSyncGifts();
});
